import { BetsQueryKeys } from '@/api/bets/queryKey';
import { useUserAccount } from '@/api/user/queries';
import { GameIDType } from '@/enums/gameId';
import { DetailedBet } from '@/models/detailed-bet';
import { GenericError } from '@/models/generic-error';
import { BetsOriginalsMeInputDto } from '@/services/dto/bets/bets-originals-me-input.dto';
import { Nullable, OneOrMany } from '@starsoft/common/interfaces';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { gamesWithManyBetsOptions } from './games-with-many-bets.options';
import {
  UseBetsOriginalsMeProps,
  UseBetsOriginalsMeQueryKeyProps,
  UseBetsOriginalsMeReturnProps,
} from './props';
import { fetchBetsOriginalsMe } from './service';

export function useBetsOriginalsMe<T extends GameIDType>(
  options?: UseBetsOriginalsMeProps<T>,
): UseBetsOriginalsMeReturnProps<T> {
  const { isAuthorized } = useUserAccount();
  const { gameId } = options ?? {};

  const filter: BetsOriginalsMeInputDto = {
    gameId,
  };

  const queryKey: UseBetsOriginalsMeQueryKeyProps = [
    BetsQueryKeys.BetsOriginalsMe,
    filter,
  ];

  const { data, isPending, fetchStatus, error, refetch, isFetching } = useQuery<
    Nullable<OneOrMany<DetailedBet>>,
    AxiosError<GenericError>,
    Nullable<OneOrMany<DetailedBet>>,
    UseBetsOriginalsMeQueryKeyProps
  >({
    queryFn: fetchBetsOriginalsMe,
    queryKey,
    staleTime: 2 * 60 * 1000,
    enabled: isAuthorized && !!gameId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    isPending: (isPending || isFetching) && fetchStatus !== 'idle',
    error,
    refetch,
    bet: gamesWithManyBetsOptions.includes(gameId)
      ? ((Array.isArray(data)
          ? (data as DetailedBet[])
          : !!data
            ? [data]
            : []) as UseBetsOriginalsMeReturnProps<T>['bet'])
      : (data as DetailedBet as UseBetsOriginalsMeReturnProps<T>['bet']),
  };
}
